import { Link } from "react-router-dom";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";
import LeadForm from "@/components/LeadForm";
import { requestBook } from "@/lib/api";

const inside = [
  { icon: "fa-eye", t: "Understand your patterns", d: "How awareness can create space between an urge and the response that follows." },
  { icon: "fa-arrows-rotate", t: "Rethink your relationship with desire", d: "Explore the idea that desire doesn't automatically mean suppression or indulgence." },
  { icon: "fa-gauge", t: "Recognize where your energy is going", d: "Identify habits and distractions that may be draining attention and momentum." },
  { icon: "fa-wind", t: "Learn practical redirection techniques", d: "Breathwork, visualization, routines, movement, journaling, and other intentional practices." },
  { icon: "fa-seedling", t: "Respond differently to setbacks", d: "Learn to treat setbacks as information that can strengthen future responses." },
  { icon: "fa-compass", t: "Build your own personal blueprint", d: "Create daily practices aligned with the person you want to become." },
];

const chapters = [
  "Sexual Energy as Life Force", "The Cost of Leakage", "From Lust to Love — Redefining Desire",
  "Awareness — The First Step", "Rituals for Redirection", "Relapse, Resistance, and Reboot",
  "Rewriting the Story", "Designing Your Transmutation Blueprint",
];

export default function Book() {
  return (
    <div className="pb-8">
      {/* Hero */}
      <Section className="pt-14 sm:pt-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-2xl border border-stone-200 max-w-sm mx-auto rotate-[-2deg]">
              <img src="/brand-reflect.png" alt="Sex Transmutation — the Sacred Flame Guidebook" className="w-full aspect-[3/4] object-cover" />
            </div>
          </div>
          <div>
            <Eyebrow>The Sacred Flame Guidebook</Eyebrow>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.06] mt-4">
              What if you didn't have to fight your sexual energy?
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed mt-5">
              Discover a different way to understand desire, break familiar patterns, and redirect that
              energy toward greater purpose, creativity, connection, and personal growth. <em>Sex
              Transmutation</em> introduces the Sacred Flame approach — awareness, intentional
              redirection, values, and growth rather than shame or simple suppression.
            </p>
            <div className="mt-6 bg-[#EEF2ED] rounded-2xl p-5 border border-[#1F3D2E]/15">
              <p className="font-playfair text-lg text-stone-900"><i className="fa-solid fa-lock text-[#1F3D2E] mr-2" />Start privately</p>
              <p className="text-stone-600 text-sm mt-1 leading-relaxed">
                You don't have to explain your situation to anyone today. Begin by reading, reflecting,
                and deciding for yourself whether this approach resonates with you.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Inside the guide */}
      <Section className="mt-20">
        <div className="max-w-2xl">
          <Eyebrow>Inside the guide</Eyebrow>
          <h2 className="font-playfair text-3xl sm:text-4xl text-stone-900 mt-3">A structured process, not general inspiration</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {inside.map((b) => (
            <div key={b.t} className="bg-white rounded-2xl border border-stone-200 p-6">
              <i className={`fa-solid ${b.icon} text-[#1F3D2E] text-lg`} />
              <h3 className="font-playfair text-lg text-stone-900 mt-3">{b.t}</h3>
              <p className="text-stone-600 text-sm mt-1.5 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-2">
          {chapters.map((c) => (
            <span key={c} className="bg-[#F0EBE0] border border-stone-200 rounded-full px-4 py-2 text-sm text-stone-700">{c}</span>
          ))}
        </div>
        <div className="mt-10 rounded-[2rem] bg-[#1F3D2E] p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <p className="overline-label text-amber-300/90">New — companion videos</p>
            <h3 className="font-serif-display text-2xl sm:text-3xl text-amber-50 mt-2">Prefer to watch? A video accompanies each chapter</h3>
            <p className="text-stone-300 text-sm mt-3 leading-relaxed max-w-xl">
              Jonathan walks you through each chapter in his own words. Watch online or download the
              videos to keep — free, with just your email.
            </p>
          </div>
          <Link to="/video-series" data-testid="book-video-cta" className="shrink-0 inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-semibold text-stone-900 hover:bg-amber-400 transition-colors">
            <i className="fa-solid fa-play" /> Watch the video series
          </Link>
        </div>
      </Section>

      {/* Email capture */}
      <Section className="mt-20" id="get-the-guide">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <Eyebrow>Get the free guidebook</Eyebrow>
            <h2 className="font-playfair text-3xl text-stone-900 mt-3">Send me the free Sacred Flame guide</h2>
            <p className="text-stone-600 leading-relaxed mt-4">
              Free digital edition, delivered privately. No phone number, no pressure — just the guide,
              yours to explore in your own time.
            </p>
            <div className="mt-6 bg-white rounded-2xl border border-stone-200 p-6">
              <p className="overline-label">About the author</p>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed">
                Jonathan Manning is the founder of TENET Transformation and creator of the Sacred Flame
                approach, focused on helping individuals understand recurring patterns and the internal
                processes that can influence behavior.
              </p>
              <Link to="/about" data-testid="book-meet-jonathan" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-3">Meet Jonathan <i className="fa-solid fa-arrow-right text-xs" /></Link>
            </div>
          </div>

          <div className="bg-white rounded-[2rem] border border-stone-200 p-8 shadow-sm">
            <LeadForm
              testIdPrefix="book"
              submitLabel="Send me the free guidebook"
              onSubmit={requestBook}
              consentLabel="I'd like the free guidebook and understand my email may be used for occasional, related follow-up."
              fields={[
                { name: "name", label: "First name", required: true, placeholder: "Your first name" },
                { name: "email", label: "Email address", type: "email", required: true, placeholder: "you@example.com" },
              ]}
              extraNote={<p className="text-xs text-stone-500 text-center">Free digital edition. Delivered directly to your inbox.</p>}
              successContent={(res) => (
                <div className="space-y-5">
                  <div>
                    <h3 className="font-playfair text-xl text-stone-900">Your guide is on its way</h3>
                    <p className="text-stone-600 text-sm mt-1">While you wait, download it now and — when you're ready — explore where you are.</p>
                  </div>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${res?.download_url || ""}`}
                    target="_blank" rel="noopener noreferrer" data-testid="book-download-button"
                    className="btn-terracotta inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
                  >
                    <i className="fa-solid fa-file-arrow-down" /> Read / download the guide
                  </a>
                  <div className="pt-4 border-t border-stone-100">
                    <p className="text-sm text-stone-700">When you're ready, take the next private step:</p>
                    <Link to="/assessments/sacred-flame-readiness" data-testid="book-assessment-cta" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-2 hover:gap-3 transition-all">
                      Take the Sacred Flame Readiness Assessment <i className="fa-solid fa-arrow-right text-xs" />
                    </Link>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
        <DisclaimerNote
          className="mt-10 max-w-3xl"
          text="This guide is provided for educational and personal-development purposes and is not intended to diagnose, treat, cure, or prevent any medical or mental-health condition. Individual experiences vary. When appropriate, individuals should seek support from qualified healthcare or mental-health professionals."
        />
      </Section>
    </div>
  );
}
